import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field } from '@/components/forge/Field'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { PlaceField } from '@/components/forge/PlaceField'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { Icon } from '@/components/forge/Icon'
import { Eyebrow, Num } from '@/components/forge/primitives'
import {
  DOC_LEVELS,
  DOC_TYPES,
  LEVEL_RANK,
  NAME_MAX_LENGTH,
  isDuplicateContent,
} from '@/features/documents/api'
import { usePlaceNodes } from '@/features/hierarchy/queries'
import { FILE_PURPOSES, acceptFor, uploadFile } from '@/features/files/api'
import { ON_TINT_CLASS, TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'
import { DocumentAttachmentPicker } from './DocumentAttachmentPicker'
import { DOC_TYPE_LOOK } from './documentLook'

const DOCUMENT = FILE_PURPOSES.document

/**
 * File a document, or edit one.
 *
 * LIVE — every field `DocumentCreate` and `DocumentUpdate` take, and nothing
 * they do not. Four steps: File → Classify → Attach → Details.
 *
 * ── The dropzone is real now ─────────────────────────────────────────────
 * The demo's was a placeholder that said so, because no file was stored
 * anywhere in that build. `document_file_id` is required and must be a
 * CONFIRMED upload of purpose `document`, so the three-step handshake in
 * `features/files/api.js` has to complete before Save is even possible. There
 * is no create-now-attach-later path.
 *
 * **PDF only, 20MB.** `FilePurpose.document` allows `application/pdf` alone.
 * The demo's type strip advertised DWG for drawings and floor plans and DOC for
 * procedures; those extensions are gone from the strip rather than left as a
 * promise the upload cannot keep. A plant that needs to file a DWG needs a
 * backend change, recorded in BACKEND-GAPS.md.
 *
 * ── Identical content is refused, and the upload is DESTROYED with it ────
 * The server hashes the bytes and refuses if the same content is already
 * attached to any of the targets chosen, calling `storage.reject_file` as it
 * does. So the file id the dialog is holding becomes dead at that moment:
 * pressing Save again fails differently and more confusingly. This is the one
 * failure that sends the person back to step 1 with the file cleared, and it is
 * the only reason `isDuplicateContent` exists.
 *
 * ── Editing replaces everything, including the file ──────────────────────
 * `DocumentUpdate` carries the same nine fields as the create and `PUT` is a
 * full replace. Keeping the current `document_file_id` leaves the file alone;
 * choosing a new one adds revision N+1. So the edit opens on the document's
 * CURRENT revision and says which — a form that guessed would silently fork the
 * history. This is also why `useDocument` holds no stale cache.
 *
 * ── Who owns it, and how deep it reaches — fixed when it is filed ────────
 * `DocumentCreate` takes `plant_id` and `level`; `DocumentUpdate` takes
 * neither. A blank `plant_id` files the document for the WHOLE ACCOUNT — it may
 * be attached anywhere — and a plant id limits it to that plant, every
 * attachment included. `level` is the deepest tier it may be attached at. Both
 * are asked on the Attach step, above the picker they fence, and shown
 * read-only on an edit.
 *
 * Filing for the whole account needs an account-level `documents.manage`
 * grant, and a plant needs a grant covering it; the options say so rather than
 * letting the server refuse (`verify_owner_plant`, keyed `plant_id`).
 *
 * ── Attach-only editing ──────────────────────────────────────────────────
 * Without a manage grant over the owner plant, the server lets the attachments
 * change and nothing else (`_require_manage_to_change`). `attachOnly` opens the
 * dialog on the Attach step with every other field locked, and the file kept.
 *
 * ── There is no revision field, and no owner, tags or language ───────────
 * `current_revision` is an integer the server assigns. The demo collected
 * "Rev C" and "v2.1" as free text, plus an owner, tags, a language and a source
 * system — none of which is a column. They are not collected here rather than
 * collected and discarded, which is the call `WorkOrderFormDialog` made about
 * its estimate field.
 */

const STEPS = ['file', 'classify', 'attach', 'details']

export function DocumentFormDialog({
  open,
  document,
  access,
  attachOnly = false,
  pending,
  error,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation(['app', 'common'])
  const editing = Boolean(document)
  const locked = editing && attachOnly
  const fileInput = useRef(null)

  const [step, setStep] = useState(locked ? STEPS.indexOf('attach') : 0)
  const [form, setForm] = useState(() => initial(document, access))
  const [file, setFile] = useState(() => initialFile(document))
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }))

  /* The owner plant's NAME on an edit, where the record holds only its id. */
  const ownerNodes = usePlaceNodes(
    editing && document.ownerPlantId ? [{ tier: 'site', id: document.ownerPlantId }] : [],
  )
  const ownerName = editing
    ? (ownerNodes.data.items.find((node) => node.id === document.ownerPlantId)?.name ??
      (ownerNodes.isPending ? t('common:loading') : document.ownerPlantId))
    : null
  /* What the picker fences on: `{id, name}` of the owner plant, or null. */
  const ownerPlant = editing
    ? document.ownerPlantId
      ? { id: document.ownerPlantId, name: ownerName }
      : null
    : form.ownerScope === 'plant' && form.ownerPlant
      ? { id: form.ownerPlant.id, name: form.ownerPlant.name }
      : null

  /* A new owner can invalidate every attachment — a line's plant is not on the
     chip — so they are cleared rather than left for the server to refuse. */
  const setOwner = (ownerScope, place) =>
    setForm((previous) => {
      const same =
        previous.ownerScope === ownerScope && previous.ownerPlant?.id === (place?.id ?? null)
      /* Account-wide reaches everywhere, so narrowing TO it keeps them. */
      const keep = same || ownerScope === 'account'
      return {
        ...previous,
        ownerScope,
        ownerPlant: place,
        attachments: keep ? previous.attachments : [],
      }
    })

  /* A shallower level drops what it would refuse. A sensor class has no rank
     and stays. */
  const setLevel = (level) =>
    setForm((previous) => ({
      ...previous,
      level,
      attachments: previous.attachments.filter(
        (one) => one.rank == null || one.rank <= LEVEL_RANK[level],
      ),
    }))

  const plantNotGranted =
    !editing &&
    form.ownerScope === 'plant' &&
    Boolean(form.ownerPlant) &&
    !access.canOwnPlant(form.ownerPlant.id)

  const ownerReady =
    editing ||
    (form.ownerScope === 'account'
      ? access.canOwnAccountWide
      : Boolean(form.ownerPlant) && !plantNotGranted)

  const choose = async (event) => {
    const chosen = event.target.files?.[0]
    // Cleared immediately, so picking the same file twice still fires `change`.
    event.target.value = ''
    if (!chosen) return

    setUploadError(null)
    setUploading(true)
    try {
      const fileId = await uploadFile(chosen, DOCUMENT)
      setFile({ fileId, name: chosen.name, replaced: true })
      /* The filename is the best guess at the title and is almost always close.
         Pre-filling is not presumptuous when the field stays editable — and it
         is not done on an EDIT, where the name is already somebody's decision. */
      if (!editing && !form.name.trim()) {
        set('name', chosen.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '))
      }
    } catch (cause) {
      setUploadError(cause?.fields?.file ?? 'failed')
    } finally {
      setUploading(false)
    }
  }

  /* Each step names its own precondition, and the button is disabled with the
     reason above it rather than enabled and then complaining — the arrangement
     the import wizard and the demo's own wizard both use. */
  const canContinue =
    step === 0
      ? Boolean(file?.fileId) && !uploading
      : step === 2
        ? form.attachments.length > 0 && ownerReady
        : true

  const valid =
    Boolean(file?.fileId) &&
    !uploading &&
    form.name.trim().length > 0 &&
    form.attachments.length > 0 &&
    ownerReady &&
    Boolean(form.effectiveDate) &&
    // Optional, but a document that expires before it takes effect is nonsense
    // the server will store without complaint. See BACKEND-GAPS.md.
    (!form.expiryDate || form.expiryDate >= form.effectiveDate)

  const duplicate = isDuplicateContent(error)
  /* The server's other field-keyed refusals, named by what they are about. Its
     English is not shown; the field it arrives under is stable. */
  const refusal = refusalOf(error)

  return (
    <ForgeDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      size="lg"
      title={editing ? t('dataFusion.docs.form.editTitle') : t('dataFusion.docs.form.newTitle')}
      description={
        editing
          ? t('dataFusion.docs.form.editDescription', { revision: document.currentRevision })
          : undefined
      }
      footer={
        <>
          <ForgeButton onClick={step === 0 ? onClose : () => setStep(step - 1)}>
            {t(step === 0 ? 'common:actions.cancel' : 'common:actions.back')}
          </ForgeButton>
          <span className="flex-1" />
          {step < STEPS.length - 1 ? (
            <ForgeButton variant="primary" disabled={!canContinue} onClick={() => setStep(step + 1)}>
              {t('common:actions.continue')}
              <Icon name="right" size="xs" className="mirror-rtl" />
            </ForgeButton>
          ) : (
            <ForgeButton
              variant="primary"
              disabled={!valid || pending}
              onClick={() =>
                onSubmit({
                  ...(editing ? { id: document.id } : {}),
                  name: form.name,
                  documentType: form.documentType,
                  effectiveDate: form.effectiveDate,
                  expiryDate: form.expiryDate,
                  attachments: form.attachments,
                  documentFileId: file.fileId,
                  ...(editing
                    ? {}
                    : {
                        ownerPlantId:
                          form.ownerScope === 'plant' ? (form.ownerPlant?.id ?? null) : null,
                        level: form.level,
                      }),
                })
              }
            >
              {editing ? t('dataFusion.docs.form.save') : t('dataFusion.docs.form.submit')}
            </ForgeButton>
          )}
        </>
      }
    >
      <Steps step={step} t={t} />

      {locked ? (
        <p className="mt-4 mb-0 max-w-[62ch] rounded-sm bg-sunken px-3.5 py-2.5 text-body-sm text-ink-dim">
          {t('dataFusion.docs.form.attachOnlyNote')}
        </p>
      ) : null}

      {/* ── 1 · The file ──────────────────────────────────────────────── */}
      {step === 0 ? (
        <div className="mt-5 flex flex-col gap-4">
          <input
            ref={fileInput}
            type="file"
            // Only what the server will take, from `PURPOSE_RULES` rather than
            // a list restated here — see `features/files/api.js`.
            accept={acceptFor(DOCUMENT)}
            onChange={choose}
            className="sr-only"
          />

          <div className="flex flex-col items-center gap-1.5 rounded-sm border border-dashed border-hairline bg-sunken px-5 py-8 text-center">
            <Icon name="doc" size="lg" className="text-ink-mute" />
            <p className="m-0 mt-1 text-body font-semibold text-ink">
              {file?.fileId ? file.name : t('dataFusion.docs.form.dropzone')}
            </p>
            <p className="m-0 text-body-sm text-ink-dim">{t('dataFusion.docs.form.dropzoneHint')}</p>
            <ForgeButton
              type="button"
              size="sm"
              className="mt-2"
              disabled={uploading || locked}
              onClick={() => fileInput.current?.click()}
            >
              <Icon name="up" size="xs" />
              {uploading
                ? t('dataFusion.docs.form.uploading')
                : file?.fileId
                  ? t('dataFusion.docs.form.chooseAnother')
                  : t('dataFusion.docs.form.choose')}
            </ForgeButton>
          </div>

          {uploadError ? (
            <p className="m-0 text-body-sm text-on-red">
              {t(`dataFusion.docs.form.uploadError.${uploadError}`, {
                defaultValue: t('dataFusion.docs.form.uploadError.failed'),
              })}
            </p>
          ) : null}

          {/* On an edit the document already has a file, and keeping it is the
              ordinary case. Which revision it is gets said out loud, because
              replacing it is not an edit of the file — it adds a new one, and
              the reader should know the number they are about to follow. */}
          {editing ? (
            <p className="m-0 rounded-sm bg-sunken px-3.5 py-2.5 text-body-sm text-ink-dim">
              {file?.replaced
                ? t('dataFusion.docs.form.willAddRevision', {
                    revision: document.currentRevision + 1,
                  })
                : t('dataFusion.docs.form.keepingRevision', {
                    revision: document.currentRevision,
                  })}
            </p>
          ) : null}

          <Field
            label={t('dataFusion.docs.form.name')}
            required
            maxLength={NAME_MAX_LENGTH}
            disabled={locked}
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder={t('dataFusion.docs.form.namePlaceholder')}
          />
        </div>
      ) : null}

      {/* ── 2 · What kind of document ─────────────────────────────────── */}
      {step === 1 ? (
        <div className="mt-5">
          <Eyebrow className="mb-2">{t('dataFusion.docs.form.type')}</Eyebrow>
          <div className="grid grid-cols-2 gap-2 max-sm:grid-cols-1">
            {DOC_TYPES.map((entry) => {
              const look = DOC_TYPE_LOOK[entry]
              const selected = form.documentType === entry
              return (
                <button
                  key={entry}
                  type="button"
                  aria-pressed={selected}
                  disabled={locked}
                  onClick={() => set('documentType', entry)}
                  className={cn(
                    'flex items-center gap-2.5 rounded-sm border px-3 py-2.5 text-start transition-colors',
                    selected
                      ? 'border-forge tint-forge font-semibold text-ink'
                      : 'border-hairline bg-card text-ink-dim enabled:hover:border-forge/40',
                    'disabled:cursor-not-allowed',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-xs',
                      look.tone ? TINT_CLASS[look.tone] : 'bg-sunken',
                    )}
                  >
                    <Icon
                      name={look.icon}
                      size="xs"
                      className={look.tone ? ON_TINT_CLASS[look.tone] : 'text-ink-mute'}
                    />
                  </span>
                  <span className="min-w-0 text-body-sm">
                    {t(`dataFusion.docs.type.${entry}`)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {/* ── 3 · What it is attached to ────────────────────────────────── */}
      {step === 2 ? (
        <div className="mt-5 flex flex-col gap-5">
          <OwnerAndLevel
            t={t}
            editing={editing}
            document={document}
            ownerName={ownerName}
            form={form}
            access={access}
            plantNotGranted={plantNotGranted}
            disabled={pending}
            onOwner={setOwner}
            onLevel={setLevel}
          />

          <DocumentAttachmentPicker
            /* Remounted per owner, so a sensor machine chosen under one plant
               cannot outlive the move to another. */
            key={ownerPlant?.id ?? 'account'}
            ownerPlant={ownerPlant}
            level={editing ? document.level : form.level}
            canAttachAssets={access.canAttachAssets}
            canAttachClasses={access.canAttachClasses}
            attachments={form.attachments}
            onChange={(next) =>
              setForm((previous) => ({
                ...previous,
                attachments: typeof next === 'function' ? next(previous.attachments) : next,
              }))
            }
            disabled={pending}
          />
        </div>
      ) : null}

      {/* ── 4 · The dates ─────────────────────────────────────────────── */}
      {step === 3 ? (
        <div className="mt-5 flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('dataFusion.docs.form.effectiveDate')}
              type="date"
              required
              disabled={locked}
              value={form.effectiveDate}
              onChange={(event) => set('effectiveDate', event.target.value)}
              hint={t('dataFusion.docs.form.effectiveDateHint')}
            />
            <Field
              label={t('dataFusion.docs.form.expiryDate')}
              type="date"
              disabled={locked}
              value={form.expiryDate}
              onChange={(event) => set('expiryDate', event.target.value)}
              hint={t('dataFusion.docs.form.expiryDateHint')}
              error={
                form.expiryDate && form.expiryDate < form.effectiveDate
                  ? t('dataFusion.docs.form.expiryBeforeEffective')
                  : undefined
              }
            />
          </div>

          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 rounded-sm bg-sunken px-3.5 py-3">
            <dt className="text-micro text-ink-dim">{t('dataFusion.docs.form.summaryFile')}</dt>
            <dd className="m-0 truncate text-body-sm text-ink">{file?.name ?? '—'}</dd>
            <dt className="text-micro text-ink-dim">{t('dataFusion.docs.form.summaryType')}</dt>
            <dd className="m-0 text-body-sm text-ink">
              {t(`dataFusion.docs.type.${form.documentType}`)}
            </dd>
            <dt className="text-micro text-ink-dim">{t('dataFusion.docs.form.summaryOwner')}</dt>
            <dd className="m-0 text-body-sm text-ink">
              {ownerPlant ? ownerPlant.name : t('dataFusion.docs.owner.account')}
            </dd>
            <dt className="text-micro text-ink-dim">{t('dataFusion.docs.form.summaryLevel')}</dt>
            <dd className="m-0 text-body-sm text-ink">
              {t(`dataFusion.docs.level.${editing ? document.level : form.level}`)}
            </dd>
            <dt className="text-micro text-ink-dim">
              {t('dataFusion.docs.form.summaryAttachments')}
            </dt>
            <dd className="m-0 text-body-sm text-ink">
              <Num>
                {t('dataFusion.docs.form.attachmentCount', { count: form.attachments.length })}
              </Num>
            </dd>
          </dl>

          {/* Where the demo's other seven fields went, said out loud rather
              than left for somebody to notice they are missing. */}
          <p className="m-0 max-w-[62ch] text-micro font-normal text-ink-dim">
            {t('dataFusion.docs.form.droppedFields')}
          </p>

          {duplicate ? (
            /* The one failure that cannot be retried from here: the server
               destroyed the uploaded object as it refused, so the held file id
               is dead. Said plainly, with the way back. */
            <div className="rounded-sm border border-hairline tint-amber px-3.5 py-3">
              <p className="m-0 text-body-sm font-semibold text-on-amber">
                {t('dataFusion.docs.form.duplicateTitle')}
              </p>
              <p className="mt-1 mb-0 max-w-[62ch] text-body-sm text-ink-dim">
                {t('dataFusion.docs.form.duplicateBody')}
              </p>
              <ForgeButton
                type="button"
                size="sm"
                className="mt-2.5"
                onClick={() => {
                  setFile(null)
                  setStep(0)
                }}
              >
                {t('dataFusion.docs.form.duplicateAction')}
              </ForgeButton>
            </div>
          ) : error ? (
            <p className="m-0 text-body-sm text-on-red">
              {t(`dataFusion.docs.form.refused.${refusal}`)}
            </p>
          ) : null}
        </div>
      ) : null}
    </ForgeDialog>
  )
}

/** Today, as the `yyyy-mm-dd` a date input wants, in the reader's own zone. */
function todayISO() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

function initial(document, access) {
  if (document) {
    return {
      name: document.name,
      documentType: document.documentType,
      effectiveDate: document.effectiveDate ?? todayISO(),
      expiryDate: document.expiryDate ?? '',
      /* Straight off the record. `AttachmentOut` already carries the name
         beside the id and the type, which is exactly the shape the picker
         holds — so an edit opens with the real selection rather than a set of
         ids it would have to resolve. */
      attachments: document.attachments.map((one) => ({ ...one })),
    }
  }
  return {
    name: '',
    documentType: DOC_TYPES[0],
    effectiveDate: todayISO(),
    expiryDate: '',
    /* The account when the reader may file there; otherwise a plant, which they
       then choose. */
    ownerScope: access.canOwnAccountWide ? 'account' : 'plant',
    ownerPlant: null,
    /* The most permissive level, so nothing is fenced off until somebody
       decides it should be. */
    level: DOC_LEVELS.at(-1),
    attachments: [],
  }
}

/**
 * Which refusal a save failed with, as a locale key.
 *
 * `plant_id` is `verify_owner_plant`; a `*_ids` key is `verify_attachment_changes`
 * (outside the grant, below the level, or outside the owner plant — one
 * sentence covers the three, since the server's English is not shown); `body`
 * is `at_least_one_attachment`. A 403 is the attach-only rule.
 */
function refusalOf(error) {
  const fields = error?.fields ?? {}
  if (fields.plant_id) return 'owner'
  if (Object.keys(fields).some((key) => key.endsWith('_ids'))) return 'attachment'
  if (error?.status === 403) return 'forbidden'
  return 'failed'
}

const SITE_ONLY = new Set(['site'])

/**
 * The owner plant and the level: chosen on a create, shown on an edit.
 *
 * The same radio-then-place arrangement Shift Master uses for a pattern's plant,
 * for the same reasons — "every plant" needs an account-level grant and is
 * disabled with the reason said otherwise, and the plant is Create-only.
 */
function OwnerAndLevel({
  t,
  editing,
  document,
  ownerName,
  form,
  access,
  plantNotGranted,
  disabled,
  onOwner,
  onLevel,
}) {
  if (editing) {
    return (
      <div className="rounded-sm border border-hairline bg-sunken px-3.5 py-3">
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5">
          <dt className="text-micro text-ink-dim">{t('dataFusion.docs.form.owner')}</dt>
          <dd className="m-0 text-body-sm text-ink">
            {document.ownerPlantId ? ownerName : t('dataFusion.docs.owner.account')}
          </dd>
          <dt className="text-micro text-ink-dim">{t('dataFusion.docs.form.level')}</dt>
          <dd className="m-0 text-body-sm text-ink">
            {t(`dataFusion.docs.level.${document.level}`)}
          </dd>
        </dl>
        <p className="mt-2 mb-0 text-micro text-ink-mute">{t('dataFusion.docs.form.ownerFixed')}</p>
      </div>
    )
  }

  const oneSite = form.ownerScope === 'plant'
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Eyebrow as="p" className="mb-2">
          {t('dataFusion.docs.form.owner')}
        </Eyebrow>

        <label className="flex cursor-pointer items-start gap-2 py-1 text-body-sm">
          <input
            type="radio"
            name="document-owner"
            checked={!oneSite}
            disabled={disabled || !access.canOwnAccountWide}
            onChange={() => onOwner('account', null)}
            className="mt-0.5 size-4 accent-[var(--hue-forge)]"
          />
          <span className="flex flex-col">
            <span className={access.canOwnAccountWide ? 'text-ink' : 'text-ink-mute'}>
              {t('dataFusion.docs.owner.account')}
            </span>
            <span className="text-micro text-ink-mute">
              {access.canOwnAccountWide
                ? t('dataFusion.docs.form.ownerAccountHint')
                : t('dataFusion.docs.form.ownerAccountNeedsGrant')}
            </span>
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-2 py-1 text-body-sm">
          <input
            type="radio"
            name="document-owner"
            checked={oneSite}
            disabled={disabled}
            onChange={() => onOwner('plant', form.ownerPlant)}
            className="mt-0.5 size-4 accent-[var(--hue-forge)]"
          />
          <span className="flex flex-col">
            <span className="text-ink">{t('dataFusion.docs.owner.plant')}</span>
            <span className="text-micro text-ink-mute">
              {t('dataFusion.docs.form.ownerPlantHint')}
            </span>
          </span>
        </label>

        {oneSite ? (
          <PlaceField
            className="mt-1 ms-6"
            label={t('dataFusion.docs.form.ownerPlant')}
            required
            value={form.ownerPlant}
            allowTiers={SITE_ONLY}
            placeholder={t('dataFusion.docs.form.ownerPlantPlaceholder')}
            disabled={disabled}
            onChange={(place) => onOwner('plant', place)}
            hint={form.attachments.length ? t('dataFusion.docs.form.ownerClears') : undefined}
            error={plantNotGranted ? t('dataFusion.docs.form.ownerNotGranted') : undefined}
          />
        ) : null}
      </div>

      <ForgeSelect
        label={t('dataFusion.docs.form.level')}
        hint={t('dataFusion.docs.form.levelHint')}
        value={form.level}
        onValueChange={onLevel}
        disabled={disabled}
        options={DOC_LEVELS.map((entry) => ({
          value: entry,
          label: t(`dataFusion.docs.form.levelOption.${entry}`),
        }))}
      />
    </div>
  )
}

/**
 * An edit starts holding the document's CURRENT file, not nothing.
 *
 * `document_file_id` is required on the update too, and sending the current one
 * is how "leave the file alone" is spelled. A form that started empty would
 * either refuse to save a name change or, worse, forget which file it was on.
 */
function initialFile(document) {
  if (!document?.latestRevision) return null
  return {
    fileId: document.latestRevision.fileId,
    name: `${document.name} · ${document.currentRevision}`,
    replaced: false,
  }
}

/** The same step indicator the bulk-import wizard uses. */
function Steps({ step, t }) {
  return (
    <ol className="m-0 flex list-none flex-wrap items-center gap-x-2 gap-y-2 p-0">
      {STEPS.map((entry, index) => {
        const done = index < step
        const current = index === step
        return (
          <li key={entry} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full text-micro font-bold',
                current && 'bg-forge text-white',
                !done && !current && 'border border-hairline bg-card text-ink-mute',
              )}
              style={
                done ? { background: 'var(--hue-green)', color: 'var(--print-white)' } : undefined
              }
            >
              {done ? <Icon name="check" size="xs" /> : <Num>{index + 1}</Num>}
            </span>
            <span
              className={cn('text-body-sm', current ? 'font-semibold text-ink' : 'text-ink-dim')}
              aria-current={current ? 'step' : undefined}
            >
              {t(`dataFusion.docs.form.steps.${entry}`)}
            </span>
            {index < STEPS.length - 1 ? (
              <span aria-hidden="true" className="mx-1 h-px w-6 bg-hairline" />
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
